import { useCallback, useMemo } from 'react';
import { type Editor, useCurrentEditor, useEditorState } from '@tiptap/react';

type Mark =
  | 'bold'
  | 'italic'
  | 'strike'
  | 'code'
  | 'underline'
  | 'superscript'
  | 'subscript';

/**
 * tiptap 라이브러리 UI와 결합도를 낮춘 hook
 */
export function useMark(config: UseMarkConfig) {
  const { editor: providedEditor, type } = config;

  const { editor: coreEditor } = useCurrentEditor();
  const mainEditor = useMemo(
    () => providedEditor || coreEditor,
    [providedEditor, coreEditor],
  );

  const editorState = useEditorState({
    editor: mainEditor,
    selector: ctx => {
      return {
        isActive: ctx.editor?.isActive(type) ?? false,
        canToggle: ctx.editor?.can().chain().toggleMark(type).run() ?? false,
      };
    },
  });

  const handleMark = useCallback(() => {
    if (!mainEditor?.isEditable || !editorState?.canToggle) return false;

    const success = toggleMark(mainEditor, type);
    return success;
  }, [mainEditor, type, editorState?.canToggle]);

  return {
    isActive: editorState?.isActive ?? false,
    handleMark,
  };
}

interface UseMarkConfig {
  editor?: Editor | null;
  type: Mark;
}

/**
 * Toggles a mark in the editor
 */
function toggleMark(editor: Editor | null, type: Mark): boolean {
  if (!editor || !editor.isEditable) return false;

  try {
    return editor.chain().focus().toggleMark(type).run();
  } catch (error) {
    console.error(`Failed to toggle mark ${type}:`, error);
    return false;
  }
}
